
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Zap, Settings, Trophy, DollarSign, BarChart3, ChevronDown, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import RestrictedAccessModal from "./RestrictedAccessModal";

const Navigation = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [restrictedModal, setRestrictedModal] = useState({ open: false, featureName: '' });
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const [currentValue, setCurrentValue] = useState<string>("");
  const isActive = (path: string) => location.pathname === path;

  // Sync hover state with NavigationMenu value
  useEffect(() => {
    setCurrentValue(hoveredMenu || "");
  }, [hoveredMenu]);

  const handleRestrictedAccess = (featureName: string, path: string, event: React.MouseEvent) => {
    if (!isAuthenticated) {
      event.preventDefault();
      setRestrictedModal({ open: true, featureName });
    }
  };

  return (
    <>
      <NavigationMenu className="hidden md:flex" value={currentValue} onValueChange={setCurrentValue}>
        <NavigationMenuList className="space-x-2">
          <NavigationMenuItem>
            <Link to="/">
              <Button 
                variant={isActive("/") ? "secondary" : "ghost"} 
                className="h-10"
              >
                Início
              </Button>
            </Link>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <Link to="/guru-chat">
              <Button
                variant={isActive("/guru-chat") ? "secondary" : "ghost"}
                className="h-10 relative font-bold bg-gradient-to-r from-amber-500/10 to-yellow-500/10 hover:from-amber-500/20 hover:to-yellow-500/20 text-amber-700 dark:text-amber-400"
              >
                🤖 Guru Chat
                <span className="absolute -top-1 -right-1 bg-amber-400 text-black text-[8px] font-extrabold px-1.5 py-0.5 rounded-full shadow">NOVO</span>
              </Button>
            </Link>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <Link to="/vitrine">
              <Button
                variant={isActive("/vitrine") ? "secondary" : "ghost"}
                className="h-10 relative font-bold bg-gradient-to-r from-yellow-500/10 to-amber-500/10 hover:from-yellow-500/20 hover:to-amber-500/20 text-yellow-700 dark:text-yellow-400"
              >
                ✨ Vitrine
                <span className="absolute -top-1 -right-1 bg-yellow-400 text-black text-[8px] font-extrabold px-1.5 py-0.5 rounded-full shadow">TOUR</span>
              </Button>
            </Link>
          </NavigationMenuItem>

          <NavigationMenuItem 
            value="gerar-jogos"
            onMouseEnter={() => setHoveredMenu('gerar-jogos')}
            onMouseLeave={() => setHoveredMenu(null)}
          >
            <NavigationMenuTrigger className="h-10 bg-transparent hover:bg-accent hover:text-accent-foreground data-[active]:bg-accent/50 data-[state=open]:bg-accent/50">
              <Zap className="mr-2 h-4 w-4" />
              Gerar Jogos IA
            </NavigationMenuTrigger>
            <NavigationMenuContent 
              onMouseEnter={() => setHoveredMenu('gerar-jogos')}
              onMouseLeave={() => setHoveredMenu(null)}
            >
              <div className="grid w-[400px] gap-3 p-4">
                <Link 
                  to="/gerar-jogos" 
                  onClick={(e) => handleRestrictedAccess("Gerar Jogos IA", "/gerar-jogos", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    <div className="text-sm font-medium leading-none">Gerar Jogos IA</div>
                  </div>
                  <p className="text-sm leading-snug text-muted-foreground">
                    Gere jogos inteligentes para o jogo do bicho
                  </p>
                </Link>
                
                <Link 
                  to="/software" 
                  onClick={(e) => handleRestrictedAccess("Software Turbinado", "/software", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    <div className="text-sm font-medium leading-none">Soft. Turb. IA</div>
                  </div>
                  <p className="text-sm leading-snug text-muted-foreground">
                    Software turbinado com inteligência artificial
                  </p>
                </Link>

                <Link 
                  to="/loterias" 
                  onClick={(e) => handleRestrictedAccess("Loterias Caixa", "/loterias", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="flex items-center gap-2">
                    <Trophy className="h-4 w-4" />
                    <div className="text-sm font-medium leading-none">Loterias Caixa</div>
                  </div>
                  <p className="text-sm leading-snug text-muted-foreground">
                    Palpites para loterias da Caixa Econômica
                  </p>
                </Link>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>

          <NavigationMenuItem 
            value="metodos"
            onMouseEnter={() => setHoveredMenu('metodos')}
            onMouseLeave={() => setHoveredMenu(null)}
          >
            <NavigationMenuTrigger className="h-10 bg-transparent hover:bg-accent hover:text-accent-foreground data-[active]:bg-accent/50 data-[state=open]:bg-accent/50">
              🎯
              Métodos
            </NavigationMenuTrigger>
            <NavigationMenuContent 
              onMouseEnter={() => setHoveredMenu('metodos')}
              onMouseLeave={() => setHoveredMenu(null)}
            >
              <div className="grid w-[450px] gap-3 p-4">
                <Link 
                  to="/metodos" 
                  onClick={(e) => handleRestrictedAccess("Métodos IA", "/metodos", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="flex items-center gap-2">
                    🎯
                    <div className="text-sm font-medium leading-none">Métodos c/ IA</div>
                  </div>
                  <p className="text-sm leading-snug text-muted-foreground">
                    Métodos exclusivos com inteligência artificial
                  </p>
                </Link>
                
                <Link
                  to="/metodos?method=centena-matadora"
                  onClick={(e) => handleRestrictedAccess("Centena Matadora", "/metodos?method=centena-matadora", e)}
                  className="relative block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors bg-gradient-to-r from-red-50 to-orange-50 hover:from-red-100 hover:to-orange-100 border border-red-300"
                >
                  <span className="absolute -top-1 -right-1 bg-yellow-400 text-black text-[9px] font-extrabold px-1.5 py-0.5 rounded-full shadow">NOVO</span>
                  <div className="flex items-center gap-2">
                    🎯
                    <div className="text-sm font-bold leading-none text-red-700">Centena Matadora</div>
                  </div>
                  <p className="text-xs leading-snug text-muted-foreground">
                    Desdobramento progressivo com 4 níveis (4/10/20/30)
                  </p>
                </Link>

                <div className="grid grid-cols-2 gap-2">
                  <Link 
                    to="/metodos?method=bio-numerico" 
                    onClick={(e) => handleRestrictedAccess("Bio Numérico Stellar", "/metodos?method=bio-numerico", e)}
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">Bio Numérico Stellar</div>
                  </Link>
                  
                  <Link 
                    to="/metodos?method=centena-bichos" 
                    onClick={(e) => handleRestrictedAccess("Centena dos Bichos", "/metodos?method=centena-bichos", e)}
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">Centena dos Bichos</div>
                  </Link>
                  
                  <Link 
                    to="/metodos?method=centenas-viciadas" 
                    onClick={(e) => handleRestrictedAccess("Centenas Viciadas", "/metodos?method=centenas-viciadas", e)}
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">Centenas Viciadas</div>
                  </Link>
                  
                  <Link 
                    to="/metodos?method=dezenas-premiadas" 
                    onClick={(e) => handleRestrictedAccess("Dezenas Premiadas", "/metodos?method=dezenas-premiadas", e)}
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">Dezenas Premiadas</div>
                  </Link>
                  
                  <Link 
                    to="/metodos?method=esquema-infalivel" 
                    onClick={(e) => handleRestrictedAccess("Esquema Infalível", "/metodos?method=esquema-infalivel", e)}
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">Esquema Infalível</div>
                  </Link>
                  
                  <Link 
                    to="/metodos?method=metodo-777" 
                    onClick={(e) => handleRestrictedAccess("Método 777", "/metodos?method=metodo-777", e)}
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">Método 777</div>
                  </Link>
                  
                  <Link 
                    to="/metodos?method=cruz-dia" 
                    onClick={(e) => handleRestrictedAccess("Cruz do Dia", "/metodos?method=cruz-dia", e)}
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">Cruz do Dia</div>
                  </Link>
                  
                  <Link 
                    to="/metodos?method=sequencia" 
                    onClick={(e) => handleRestrictedAccess("Método Sequência", "/metodos?method=sequencia", e)}
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">Método Sequência</div>
                  </Link>
                  
                  <Link 
                    to="/metodos?method=soma-chave-ouro" 
                    onClick={(e) => handleRestrictedAccess("Soma Chave de Ouro", "/metodos?method=soma-chave-ouro", e)}
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">Soma Chave de Ouro</div>
                  </Link>
                  
                  <Link 
                    to="/metodos?method=calculadora-chave-ouro" 
                    onClick={(e) => handleRestrictedAccess("Calculadora Chave de Ouro", "/metodos?method=calculadora-chave-ouro", e)}
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">Calculadora Chave de Ouro</div>
                  </Link>

                  <Link to="/metodos?method=desdobramento-jb" onClick={(e) => handleRestrictedAccess("Desdobramento JB", "/metodos?method=desdobramento-jb", e)} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                    <div className="text-sm font-medium leading-none">Desdobramento JB</div>
                  </Link>
                  <Link to="/metodos?method=sistema-ia-bicho" onClick={(e) => handleRestrictedAccess("Sistema IA Bicho", "/metodos?method=sistema-ia-bicho", e)} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                    <div className="text-sm font-medium leading-none">Sistema IA Bicho</div>
                  </Link>
                  <Link to="/metodos?method=7-grupos" onClick={(e) => handleRestrictedAccess("7 Grupos", "/metodos?method=7-grupos", e)} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                    <div className="text-sm font-medium leading-none">7 Grupos</div>
                  </Link>
                  <Link to="/metodos?method=bionumerico-stellar" onClick={(e) => handleRestrictedAccess("BioNumérico Stellar", "/metodos?method=bionumerico-stellar", e)} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                    <div className="text-sm font-medium leading-none">BioNumérico Stellar</div>
                  </Link>
                  <Link to="/metodos?method=numerologia-nome" onClick={(e) => handleRestrictedAccess("Numerologia do Nome", "/metodos?method=numerologia-nome", e)} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                    <div className="text-sm font-medium leading-none">Numerologia do Nome</div>
                  </Link>
                  <Link to="/metodos?method=achar-centena-dia" onClick={(e) => handleRestrictedAccess("Achar Centena do Dia", "/metodos?method=achar-centena-dia", e)} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                    <div className="text-sm font-medium leading-none">Achar Centena do Dia</div>
                  </Link>
                </div>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>

          <NavigationMenuItem 
            value="resultados"
            onMouseEnter={() => setHoveredMenu('resultados')}
            onMouseLeave={() => setHoveredMenu(null)}
          >
            <NavigationMenuTrigger className="h-10 bg-transparent hover:bg-accent hover:text-accent-foreground data-[active]:bg-accent/50 data-[state=open]:bg-accent/50">
              <BarChart3 className="mr-2 h-4 w-4" />
              Resultados
            </NavigationMenuTrigger>
            <NavigationMenuContent 
              onMouseEnter={() => setHoveredMenu('resultados')}
              onMouseLeave={() => setHoveredMenu(null)}
            >
              <div className="grid w-[350px] gap-3 p-4">
                <Link 
                  to="/resultados-bicho" 
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    <div className="text-sm font-medium leading-none">Resultado RIO</div>
                  </div>
                  <p className="text-sm leading-snug text-muted-foreground">
                    Resultados do jogo do bicho do Rio de Janeiro
                  </p>
                </Link>
                
                <Link 
                  to="/loterias" 
                  onClick={(e) => handleRestrictedAccess("Loterias Caixa", "/loterias", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="flex items-center gap-2">
                    <Trophy className="h-4 w-4" />
                    <div className="text-sm font-medium leading-none">Loterias Caixa</div>
                  </div>
                  <p className="text-sm leading-snug text-muted-foreground">
                    Resultados das loterias da Caixa
                  </p>
                </Link>

                <Link 
                  to="/estatisticas" 
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    <div className="text-sm font-medium leading-none">Estatísticas</div>
                  </div>
                  <p className="text-sm leading-snug text-muted-foreground">
                    Análise estatística dos resultados
                  </p>
                </Link>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <Link to="/estatisticas">
              <Button 
                variant="ghost" 
                className="h-10 flex items-center gap-2 estatisticas-destaque"
              >
                <BarChart3 className="h-4 w-4" />
                <span>Estatísticas</span>
              </Button>
            </Link>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <Link to="/loja">
              <Button 
                variant="ghost" 
                className="h-10 flex items-center gap-2 loja-vibrante"
              >
                <ShoppingBag className="h-4 w-4" />
                <span>Loja</span>
              </Button>
            </Link>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <Link to="/pagamento">
              <Button 
                variant={isActive("/pagamento") ? "secondary" : "ghost"} 
                className="h-10"
              >
                💳 Pagamento
              </Button>
            </Link>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>

      <RestrictedAccessModal
        open={restrictedModal.open}
        onClose={() => setRestrictedModal({ open: false, featureName: '' })}
        featureName={restrictedModal.featureName}
      />
    </>
  );
};

export default Navigation;
